import * as React from 'react';
import './SwitchToClientsAccount.scss';
import { Debounce } from 'react-throttle';
import Button from 'src/components/common/Button';
import Loader from 'src/components/common/Loader';
import Alert from 'src/pages/ForgotPasswordView/components/Alert';
import AccountCard from 'src/components/AccountSwitch/components/AccountCard';
import env from 'src/shared/env';
import { goToHome, goToVerify, goToLogin } from 'src/utils/navigation';
import TablePaginator from 'src/components/common/TablePagination';

interface ISwitchToClientsAccountProps {
  isLoading: boolean;
  hasError: boolean;
  accounts: any[];
  accountsPagination: IPagination;
  isLoggedIn: boolean;
  shouldVerify: boolean;
  shouldSelectAccount: boolean;
  accountHasChanged: boolean;
  onClearError(): void;
  getAccounts(search: string, page?: number, size?: number): void;
  changeActiveAccount(newAccountId: string, clientIdentity: string): void;
  changeActiveAccountFlag(flag: boolean): void;
}

interface IPagination {
  pageNumber: number;
  total: number;
  size: number;
}

interface ISwitchToClientsAccountState {
  searchText: string;
  page: number;
}

class SwitchToClientsAccount extends React.Component<
  ISwitchToClientsAccountProps,
  ISwitchToClientsAccountState
> {
  constructor(props: any) {
    super(props);
    
    this.state = {
      searchText: '',
      page: 0
    };
  }

  private enableVerify = parseInt(env.REACT_APP_ENABLE_VERIFY_ACCOUNT, 10);

  private redirectToHome = () => {
    if (this.enableVerify && this.props.isLoggedIn && this.props.shouldVerify) {
      goToVerify();
      return;
    }
    if (this.props.isLoggedIn && this.props.shouldSelectAccount) {
      goToLogin();
      return;
    }
    if (this.props.isLoggedIn && !this.props.shouldSelectAccount) {
      goToHome();
    }
  };

  private onSearchTextChange = (searchText: string) => {
    this.setState({ searchText });
    this.props.getAccounts(this.state.searchText);
  };

  private onClicSearchText = () => {
    this.props.getAccounts(this.state.searchText);
  };

  private renderAccounts = () => {
    let items = [];
    items = this.props.accounts.map((item: any, index: any) => {
      return (
        <AccountCard
          key={index}
          accountId={item.accountId}
          userId={item.userId}
          accountName={item.name}
          isActive={false}
          action={() => {
            this.props.changeActiveAccount(item.accountId, item.userId);
          }}
        />
      );
    });
    return items;
  };

  private handlePagination = (type: 'next' | 'previous') => {
    const { accountsPagination: pagination } = this.props;
    if (type === 'previous') {
      if (pagination.pageNumber !== 0) {
        this.setState({ page: pagination.pageNumber - 1 }, () => {
          this.props.getAccounts(
            this.state.searchText,
            this.state.page,
            pagination.size
          );
        });
      }
    } else {
      if (pagination.pageNumber !== pagination.total) {
        this.setState({ page: pagination.pageNumber + 1 }, () => {
          this.props.getAccounts(
            this.state.searchText,
            this.state.page,
            pagination.size
          );
        });
      }
    }
  };

  public componentDidMount = () => {
    this.props.getAccounts('');
    this.props.changeActiveAccountFlag(false);
  };

  public componentDidUpdate(prevProps: ISwitchToClientsAccountProps) {
    if (
      this.props.accountHasChanged !== prevProps.accountHasChanged &&
      this.props.accountHasChanged
    ) {
      this.redirectToHome();
    }
  }

  public render() {
    const { pageNumber, total, size } = this.props.accountsPagination;
    return (
      <div className="rp-switch-to-clients-account">
        {this.props.isLoading ? <Loader /> : null}

        {this.props.hasError ? (
          <Alert
            type="error"
            icon="rpds-error"
            onClose={this.props.onClearError}
          >
            We couldn't change your password, please make sure you entered your
            current password correctly.
          </Alert>
        ) : null}
        <div className="rp-switch-to-clients-account--content">
          <div>
            <h1>Switch to a Client's Account</h1>
            <p className="rp-switch-to-clients-account--subtitle">
              Please type the account number you want to switch to:
            </p>
          </div>
          <div className="rp-switch-to-clients-account--search">
            <Debounce time="500" handler="onChange">
              <input
                className="search-input"
                type="text"
                onChange={e => this.onSearchTextChange(e.target.value)}
                placeholder="Search by client's account or name"
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter') {
                    this.onClicSearchText();
                  }
                }}
              />
            </Debounce>
            <Button
              label="Search"
              textTransform="capitalize"
              buttonClass="primary-button"
              icon={{
                position: 'left',
                icon: 'rpds rpds-search'
              }}
              action={this.onClicSearchText}
            />
          </div>
          {this.props.accounts.length > 0 ? (
            <div className="rp-switch-to-clients-account--accounts">
              {this.renderAccounts()}
            </div>
          ) : (
            <p className="rp-switch-to-clients-account--subtitle">
              Client's account results will appear here.
            </p>
          )}
          <TablePaginator
            handlePagination={this.handlePagination}
            pagination={{ pageNumber, pageSize: size, totalPages: total }}
          />
        </div>
      </div>
    );
  }
}

export default SwitchToClientsAccount;
